import { WorkspaceQueryParamsPipe } from "app/core/services/workspace/workspace-query-params.pipe";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from "@angular/router";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { Subject, filter, map, switchMap, takeUntil } from "rxjs";

import { NavigationService } from "app/core/services/navigation/navigation.service";
import { NavFavoritesService } from "app/core/services/navigation/nav-favorites.service";
import { VlpNavItem, Navigation } from "app/core/services/navigation/navigation.types";
import { AppStorageService } from "shared-service/app-storage/app.storage";
import { LanguagesComponent } from "shared-ui/components/languages/languages.component";
import { NotificationInboxComponent } from "app/layout/common/notification-inbox/notification-inbox.component"; // LUL-653
import { UserComponent } from "app/layout/common/user/user.component";
import {
    DEFAULT_BRAND_TITLE,
    resolveBrandNavIcon,
    resolveBrandNavLogo,
    resolveBrandTitle,
} from "app/core/services/branding/branding-assets";
import { TenantBrandingService } from "app/core/services/branding/tenant-branding.service";
import { AccountService } from "app/core/services/api/account-management/individual/account.service";
import { CurrentTenantService } from "app/core/services/api/platform-management/current-tenant.service";
import { AccountInfo } from "shared-domains/domains/account-management/individual/account.model";
import { TenantWorkspaceSelectComponent } from "app/shared/tenant-workspace-select/tenant-workspace-select.component";
import { BulkImportDockComponent } from "../common/bulk-import-dock/bulk-import-dock.component";
import { BulkImportHostComponent } from "../common/bulk-import-host/bulk-import-host.component";
import { BackendAvailabilityService } from "shared-service/services/reliability/backend-availability.service";
import { ApplicationShellComponent } from 'shared-ui/layout/application-shell/application-shell.component';

type Crumb = { label: string; url: string };

/**
 * framework-independent application shell for VLP (mirrors driving-school's custom layout):
 * plain Angular + signals + flexbox + Tailwind. Renders a collapsible sidebar
 * (mobile drawer) with the privilege-driven navigation, a header with
 * breadcrumbs + language/notifications/user, and the routed content.
 */
@Component({
    selector: "vlp-layout",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./vlp-layout.component.html",
    styleUrls: ["./vlp-layout.component.scss"],
    imports: [WorkspaceQueryParamsPipe,
        CommonModule,
        RouterModule,
        TranslocoModule,
        LanguagesComponent,
        NotificationInboxComponent,
        UserComponent,
        TenantWorkspaceSelectComponent,
        BulkImportDockComponent,
        BulkImportHostComponent,
        ApplicationShellComponent,
    ],
})
export class VlpLayoutComponent implements OnInit, OnDestroy {
    // -----------------------------------------------------------------------------------------------------
    // @ Injected services, static keys & reactive state (signals + computed navigation)
    // -----------------------------------------------------------------------------------------------------
    private readonly navService = inject(NavigationService);
    readonly fav = inject(NavFavoritesService);
    private readonly storage = inject(AppStorageService);
    private readonly branding = inject(TenantBrandingService);
    private readonly accountService = inject(AccountService);
    private readonly currentTenant = inject(CurrentTenantService);
    readonly backendAvailability = inject(BackendAvailabilityService);

    /** id nhóm ảo "Yêu thích" (đặt đầu danh sách menu). */
    static readonly FAVORITES_GROUP_ID = "__favorites__";
    private static readonly COLLAPSED_KEY = "lul.nav.collapsed";
    private static readonly COLLAPSED_GROUPS_KEY = "lul.nav.collapsedGroups";
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly transloco = inject(TranslocoService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly _unsubscribeAll = new Subject<void>();

    sidebarOpen = false; // tablet/mobile drawer
    collapsed = false; // desktop icon-only
    logoUrl = "";
    navLogoUrl = "";
    crumbs: Crumb[] = [];
    readonly accountName = signal(this.getStoredAccountName());
    readonly workspaceName = signal(DEFAULT_BRAND_TITLE);
    readonly workspaceMeta = signal("workspace-scope-platform");
    readonly canSelectWorkspaceTenant = signal(false);

    /** Navigation groups (type === 'group', with children) from the privilege menu. */
    readonly groups = signal<VlpNavItem[]>([]);

    /** Quick menu filter (diacritic-insensitive), mirrors DSS. */
    readonly menuFilter = signal("");

    readonly collapsedGroups = signal<Set<string>>(new Set<string>());

    /** Nhóm ảo "Yêu thích": gom các item (theo link) đã ghim, đặt đầu danh sách. Null nếu chưa ghim gì. */
    readonly favoriteGroup = computed<VlpNavItem | null>(() => {
        const favSet = new Set(this.fav.routes());
        if (!favSet.size) return null;
        const items: VlpNavItem[] = [];
        for (const item of this.groups()) {
            for (const i of item.children ?? []) {
                if (i.link && favSet.has(i.link)) items.push(i);
            }
        }
        if (!items.length) return null;
        return {
            id: VlpLayoutComponent.FAVORITES_GROUP_ID,
            title: "nav-favorites",
            type: "group",
            icon: "heroicons_outline:star",
            children: items,
        };
    });

    readonly filteredGroups = computed<VlpNavItem[]>(() => {
        const fav = this.favoriteGroup();
        const base = fav ? [fav, ...this.groups()] : this.groups();
        const query = VlpLayoutComponent.normalize(this.menuFilter().trim());
        if (!query) return base;
        const callback = (key: string) => VlpLayoutComponent.normalize(this.transloco.translate(key));
        return base
            .map((item) => ({ ...item, children: callback(item.title ?? "").includes(query) ? item.children : (item.children ?? []).filter((i) => callback(i.title ?? "").includes(query)) }))
            .filter((item) => (item.children?.length ?? 0) > 0);
    });

    // -----------------------------------------------------------------------------------------------------
    // @ Menu & sidebar — UI actions, accessors & collapse-state helpers
    // -----------------------------------------------------------------------------------------------------

    /** Item trong nhóm Yêu thích không hiển thị highlight active (tránh trùng với item gốc). */
    isFavoriteGroup(id: string | undefined): boolean {
        return id === VlpLayoutComponent.FAVORITES_GROUP_ID;
    }

    toggleFavorite(route: string | undefined, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (route) this.fav.toggle(route);
    }

    toggleShellFavorite(route: string): void { this.fav.toggle(route); }

    /** Leaf routes that are also a prefix of another route must match exactly to avoid double-highlight. */
    readonly exactRoutes = computed<Set<string>>(() => {
        const routes = this.groups().flatMap((item) => (item.children ?? []).map((i) => i.link ?? ""));
        const exact = new Set<string>();
        for (const value of routes) {
            if (value && routes.some((value2) => value2 !== value && value2.startsWith(value + "/"))) exact.add(value);
        }
        return exact;
    });

    activeOptions(link: string): { exact: boolean } {
        return { exact: this.exactRoutes().has(link) };
    }

    /** Server-provisioned routes may omit the leading slash. Always navigate from the app root. */
    navigationLink(link: string | undefined): string {
        const normalized = (link ?? "").split(/[?#]/)[0].split("/").filter(Boolean).join("/");
        return normalized ? `/${normalized}` : "/";
    }

    /** Tablet and mobile use an overlay drawer so the page keeps its usable width. */
    get isOverlaySidebar(): boolean {
        return window.innerWidth <= 1024;
    }

    private static normalize(item: string): string {
        return (item || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d");
    }

    onMenuFilter(value: string): void {
        this.menuFilter.set(value);
    }

    clearMenuFilter(): void {
        this.menuFilter.set("");
    }

    isGroupCollapsed(id: string): boolean {
        return !this.menuFilter() && this.collapsedGroups().has(id);
    }

    toggleGroup(id: string): void {
        const next = new Set(this.collapsedGroups());
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        this.collapsedGroups.set(next);
        this.persistCollapsedGroups(next);
    }

    private persistCollapsedGroups(set: Set<string>): void {
        try {
            localStorage.setItem(VlpLayoutComponent.COLLAPSED_GROUPS_KEY, JSON.stringify([...set]));
        } catch {
            /* bỏ qua nếu localStorage bị chặn */
        }
    }

    private restoreCollapsedState(): void {
        try {
            this.collapsed = localStorage.getItem(VlpLayoutComponent.COLLAPSED_KEY) === "true";
            const raw = localStorage.getItem(VlpLayoutComponent.COLLAPSED_GROUPS_KEY);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) this.collapsedGroups.set(new Set<string>(arr.filter((x) => typeof x === "string")));
            }
        } catch {
            /* localStorage lỗi → dùng mặc định */
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks & bootstrap helpers
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.applyBrand();

        // Thương hiệu tenant về sau lúc khởi động (REST ẩn danh) hoặc ngay sau khi quản trị viên lưu
        // logo mới. Không nghe ở đây thì sidebar giữ logo mặc định cho tới lần tải lại trang.
        this.branding.branding$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.applyBrand();
            this.refreshPageContext();
            this.cdr.detectChanges();
        });

        // Khôi phục trạng thái thu gọn (sidebar + từng nhóm) + nạp menu yêu thích roaming.
        this.restoreCollapsedState();
        this.fav.load();

        this.navService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.groups.set((navigation?.default ?? []).filter((item) => item.type === "group"));
            this.refreshPageContext();
            this.cdr.detectChanges();
        });

        this.refreshPageContext();
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.refreshPageContext();
            this.sidebarOpen = false;
            this.cdr.detectChanges();
        });
        // Language changes fire before the asynchronously loaded dictionary is ready.
        // Cancel superseded loads so rapid switches cannot restore an older title.
        this.transloco.langChanges$.pipe(
            switchMap(lang => this.transloco.load(lang).pipe(map(() => lang))),
            filter(lang => lang === this.transloco.getActiveLang()),
            takeUntil(this._unsubscribeAll),
        ).subscribe(() => { this.refreshPageContext(); this.cdr.markForCheck(); });

        this.accountService.account$.pipe(takeUntil(this._unsubscribeAll)).subscribe((account) => {
            this.accountName.set(this.resolveAccountName(account));
            this.cdr.detectChanges();
        });

        this.currentTenant.context$().pipe(takeUntil(this._unsubscribeAll)).subscribe((context) => {
            const tenant = context.PlatformTenant;
            const accountTenant = context.AccountTenant;
            this.canSelectWorkspaceTenant.set(context.CanSelectTenant);

            if (!tenant && context.CanSelectTenant) {
                this.workspaceName.set(DEFAULT_BRAND_TITLE);
                this.workspaceMeta.set("workspace-scope-platform");
            } else {
                this.workspaceName.set(tenant?.Name?.trim() || accountTenant?.Name?.trim() || DEFAULT_BRAND_TITLE);
                this.workspaceMeta.set(tenant?.Code?.trim() || accountTenant?.Code?.trim() || "workspace-scope-tenant");
            }

            // Tenant chưa có tên miền riêng thì bước nạp theo Host lúc khởi động không suy ra được gì —
            // đây là lần đầu tiên biết chắc tenant nào, nên nạp lại thương hiệu từ đây.
            const tenantId = Number(context.AccountTenantId) || 0;
            if (tenantId > 0 && tenantId !== this.brandedTenantId) {
                this.brandedTenantId = tenantId;
                this.branding.loadForTenant(tenantId).subscribe();
            }

            this.cdr.detectChanges();
        });
    }

    /** Chặn gọi lại gRPC thương hiệu mỗi lần context tenant phát lại cùng một giá trị. */
    private brandedTenantId = 0;

    private resolveAccountName(account: AccountInfo | null | undefined): string {
        return account?.FullName?.trim()
            || account?.Username?.trim()
            || this.getStoredAccountName();
    }

    private getStoredAccountName(): string {
        return this.storage.displayname?.trim()
            || this.storage.username?.trim()
            || "VLP user";
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Sidebar UI actions
    // -----------------------------------------------------------------------------------------------------

    @HostListener("window:resize")
    onResize(): void {
        if (!this.isOverlaySidebar) this.sidebarOpen = false;
        this.cdr.detectChanges();
    }

    toggleSidebar(): void {
        if (this.isOverlaySidebar) {
            this.sidebarOpen = !this.sidebarOpen;
        } else {
            this.collapsed = !this.collapsed;
            if (this.collapsed) this.menuFilter.set("");
            try {
                localStorage.setItem(VlpLayoutComponent.COLLAPSED_KEY, String(this.collapsed));
            } catch {
                /* bỏ qua nếu localStorage bị chặn */
            }
        }
        this.cdr.detectChanges();
    }

    closeSidebar(): void {
        if (this.isOverlaySidebar) {
            this.sidebarOpen = false;
            this.cdr.detectChanges();
        }
    }

    /** SL-131: when the desktop rail is collapsed the toggle button is hidden, so
     * clicking the brand/logo expands the sidebar again. No-op in any other state. */
    onBrandClick(): void {
        if (this.collapsed && !this.isOverlaySidebar) this.toggleSidebar();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods — breadcrumbs & active-item resolution
    // -----------------------------------------------------------------------------------------------------

    private buildCrumbs(route: ActivatedRoute, url = "", acc: Crumb[] = []): Crumb[] {
        if (!route) return acc;
        const rc = route.routeConfig;
        const rcPath = rc?.path ?? "";
        const segFromConfig = !rcPath
            ? ""
            : rcPath.split("/").map((params2) => (params2.startsWith(":") ? route.snapshot.params[params2.slice(1)] : params2)).join("/");
        const segFromSnapshot = route.snapshot.url.map((item) => item.path).join("/");
        const seg = segFromConfig || segFromSnapshot;

        let nextUrl = url;
        if (seg) nextUrl = `${url}/${seg}`;

        // Nhãn breadcrumb: ưu tiên data.breadcrumb; nếu không, tra bảng dịch dùng chung
        // `breadcrumb.<segment>` (bỏ qua phần id thuần số như "detail/23" → "detail");
        // cuối cùng mới rơi về segment thô để không vỡ các route cũ.
        const keyBase = seg
            .split("/")
            .filter((params) => params && !/^\d+$/.test(params))
            .pop();

        let label: string | undefined = route.snapshot.data?.["breadcrumb"];
        if (!label && keyBase) {
            const mapKey = `breadcrumb.${keyBase}`;
            label = this.transloco.translate(mapKey) !== mapKey ? mapKey : keyBase;
        }

        if (label) {
            const isFirst = acc.length === 0;
            const effectiveUrl = isFirst ? "" : nextUrl;
            const last = acc[acc.length - 1];
            // Skip a crumb that renders to the same text as the previous one — e.g.
            // dashboards/home, where both "dashboards" and "home" translate to "Trang chủ"
            // (would otherwise show "Trang chủ > Trang chủ").
            const sameAsPrev = last && this.transloco.translate(last.label) === this.transloco.translate(label);
            if (!sameAsPrev) {
                acc.push({ label, url: effectiveUrl });
            }
        }

        if (route.firstChild) return this.buildCrumbs(route.firstChild, nextUrl, acc);
        return acc;
    }

    /** Tên thương hiệu dùng làm hậu tố tiêu đề tab; rơi về mặc định khi tenant chưa cấu hình. */
    private brandTitle(): string {
        return resolveBrandTitle(DEFAULT_BRAND_TITLE);
    }

    private applyBrand(): void {
        this.logoUrl = resolveBrandNavIcon(this.storage.logoDefault);
        this.navLogoUrl = resolveBrandNavLogo(this.storage.logoDefault);
    }

    private refreshPageContext(): void {
        const routeCrumbs = this.buildCrumbs(this.activatedRoute.root);
        const activeItem = this.findActiveNavigationItem(this.router.url);
        if (activeItem?.title) {
            const activeCrumb: Crumb = { label: activeItem.title, url: "" };
            const parent = this.groups().find((group) => (group.children ?? []).some((item) => item === activeItem));
            this.crumbs = parent?.title && this.transloco.translate(parent.title) !== this.transloco.translate(activeItem.title)
                ? [{ label: parent.title, url: "" }, activeCrumb]
                : [activeCrumb];
            this.updateDocumentTitle(activeItem.title);
            return;
        }
        this.crumbs = routeCrumbs;
        const fallback = routeCrumbs[routeCrumbs.length - 1]?.label;
        this.updateDocumentTitle(fallback);
    }

    private updateDocumentTitle(key?: string): void {
        const translated = key ? this.transloco.translate<string>(key) : '';
        // Do not expose a translation key while a dictionary is still loading.
        document.title = translated && translated !== key
            ? `${translated} | ${this.brandTitle()}` : this.brandTitle();
    }

    private findActiveNavigationItem(rawUrl: string): VlpNavItem | undefined {
        const normalizedUrl = `/${rawUrl.split(/[?#]/)[0].split("/").filter(Boolean).join("/")}`.replace(/\/$/, "");
        const url = normalizedUrl === "/dashboards/overview" ? "/dashboards/home" : normalizedUrl;
        return this.groups()
            .flatMap((group) => group.children ?? [])
            .filter((item) => !!item.link)
            .map((item) => ({ item, link: `/${item.link!.split(/[?#]/)[0].split("/").filter(Boolean).join("/")}`.replace(/\/$/, "") }))
            .filter(({ link }) => url === link || url.startsWith(`${link}/`))
            .sort((left, right) => right.link.length - left.link.length)[0]?.item;
    }
}
